import { useCallback, useContext, useMemo, useReducer } from "react";

import { DocumentNode } from "@apollo/client";
import { format } from "date-fns";
import { useNavigate } from "react-router";

import { DialogContext } from "@/components/layout/DialogProvider";
import { ExpirationState } from "@/components/pages_logged_in/certifications/CertificationExpirationForm";
import { Expiration } from "@/components/pages_logged_in/certifications/types";
import {
  formatValidity,
  toExpiration,
} from "@/components/pages_logged_in/certifications/utils";
import {
  ExpirationInterval,
  ExpirationType,
  useCreateCertificationMutation,
} from "@/graphql";
import { Workspace } from "@/types/workspaces";
import { formatErrors } from "@/utils/array";

const generateCertificateThumbnail = () =>
  import("@/components/pages_logged_in/certifications/generateThumbnail").then(
    (module) => module.generateCertificateThumbnail,
  );

export type CreateCertificationReturn = {
  name: string;
  description: string;
  expiration: ExpirationState;
  preview: { name: string; description: string; expiration: Expiration };
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onExpirationChange: (expiration: ExpirationState) => void;
  onOpenChange: (value: boolean) => void;
  onCreate: () => void;
  loading: boolean;
  errorMessage: string | null;
  disabledText: string | undefined;
};

export const nameMaxLength = 60;
export const descriptionMaxLength = 300;

const initialState = {
  name: "",
  description: "",
  expiration: {
    type: ExpirationType.NoExpiration,
    relativeExpirationValue: null as number | null,
    relativeExpirationInterval: ExpirationInterval.Year,
    absoluteExpiration: null as string | null,
  },
  currentPreviewTimeout: null as NodeJS.Timeout | null,
  preview: {
    name: "",
    description: "",
    expiration: { type: ExpirationType.NoExpiration } as Expiration,
  },
  loading: false,
  errorMessage: null as string | null,
};

type Action =
  | {
      type: "SET_NAME";
      name: string;
      currentPreviewTimeout: NodeJS.Timeout;
    }
  | {
      type: "SET_DESCRIPTION";
      description: string;
      currentPreviewTimeout: NodeJS.Timeout;
    }
  | {
      type: "SET_PREVIEW";
      preview: {
        name: string;
        description: string;
        expiration: Expiration;
      };
    }
  | {
      type: "SET_PREVIEW_EXPIRATION_TYPE";
      previewExpirationType: Expiration["type"];
    }
  | {
      type: "SET_EXPIRATION";
      expiration: ExpirationState;
      currentPreviewTimeout: NodeJS.Timeout;
    }
  | {
      type: "SET_LOADING";
      loading: boolean;
    }
  | {
      type: "SET_ERROR";
      errorMessage: string | null;
    }
  | {
      type: "CREATE_COMPLETE";
      errorMessage: string | null;
    }
  | {
      type: "CREATE_START";
    }
  | {
      type: "CREATE_SUCCESS";
    }
  | {
      type: "RESET";
    };

const previewDelay = 500;

const reducer = (
  state: typeof initialState,
  action: Action,
): typeof initialState => {
  switch (action.type) {
    case "SET_NAME":
      if (state.currentPreviewTimeout) {
        clearTimeout(state.currentPreviewTimeout);
      }
      return {
        ...state,
        name: action.name,
        currentPreviewTimeout: action.currentPreviewTimeout,
        errorMessage: null,
      };
    case "SET_DESCRIPTION":
      if (state.currentPreviewTimeout) {
        clearTimeout(state.currentPreviewTimeout);
      }
      return {
        ...state,
        description: action.description,
        currentPreviewTimeout: action.currentPreviewTimeout,
        errorMessage: null,
      };
    case "SET_PREVIEW":
      return { ...state, preview: action.preview, currentPreviewTimeout: null };
    case "SET_PREVIEW_EXPIRATION_TYPE":
      return {
        ...state,
        preview: {
          ...state.preview,
          expiration:
            action.previewExpirationType === ExpirationType.NoExpiration
              ? { type: ExpirationType.NoExpiration }
              : action.previewExpirationType === ExpirationType.Relative
                ? {
                    type: ExpirationType.Relative,
                    value: state.expiration.relativeExpirationValue ?? 0,
                    interval:
                      state.expiration.relativeExpirationInterval ??
                      ExpirationInterval.Day,
                  }
                : {
                    type: ExpirationType.Absolute,
                    value: state.expiration.absoluteExpiration ?? "",
                  },
        },
        currentPreviewTimeout: null,
      };
    case "SET_EXPIRATION":
      return {
        ...state,
        currentPreviewTimeout: action.currentPreviewTimeout ?? null,
        expiration: action.expiration,
        errorMessage: null,
      };
    case "SET_LOADING":
      return { ...state, loading: action.loading };
    case "SET_ERROR":
      return { ...state, errorMessage: action.errorMessage };
    case "CREATE_COMPLETE":
      return { ...state, loading: false, errorMessage: action.errorMessage };
    case "CREATE_START":
      return { ...state, loading: true, errorMessage: null };
    case "CREATE_SUCCESS":
    case "RESET":
      if (state.currentPreviewTimeout) {
        clearTimeout(state.currentPreviewTimeout);
      }
      return initialState;
  }
};

export function useCreateCertification({
  refetchQueries,
  workspace,
}: {
  refetchQueries: { query: DocumentNode; variables: Record<string, unknown> }[];
  workspace?: Workspace;
}): CreateCertificationReturn {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const { showConfirmationDialog } = useContext(DialogContext);

  const disabledText = useMemo(() => {
    return state.name.length === 0 &&
      state.description.length > descriptionMaxLength
      ? `The certification needs a title and the description is too long`
      : state.name.length === 0
        ? `The certification needs a title`
        : state.name.length > nameMaxLength &&
            state.description.length > descriptionMaxLength
          ? `The title and description are too long`
          : state.name.length > nameMaxLength
            ? `The title is too long`
            : state.description.length > descriptionMaxLength
              ? `The description is too long`
              : undefined;
  }, [state.name, state.description]);

  const handleNameChange = useCallback(
    (name: string) => {
      const timeout = setTimeout(() => {
        dispatch({
          type: "SET_PREVIEW",
          preview: {
            ...state.preview,
            name: name.substring(0, nameMaxLength),
          },
        });
      }, previewDelay);
      dispatch({
        type: "SET_NAME",
        name,
        currentPreviewTimeout: timeout,
      });
    },
    [state.preview, dispatch],
  );

  const handleDescriptionChange = useCallback(
    (description: string) => {
      const timeout = setTimeout(() => {
        dispatch({
          type: "SET_PREVIEW",
          preview: {
            ...state.preview,
            description: description.substring(0, descriptionMaxLength),
          },
        });
      }, previewDelay);
      dispatch({
        type: "SET_DESCRIPTION",
        description,
        currentPreviewTimeout: timeout,
      });
    },
    [state.preview, dispatch],
  );

  const handleExpirationChange = useCallback(
    (expiration: ExpirationState) => {
      const timeout = setTimeout(() => {
        dispatch({
          type: "SET_PREVIEW_EXPIRATION_TYPE",
          previewExpirationType: expiration.type,
        });
      }, previewDelay);
      dispatch({
        type: "SET_EXPIRATION",
        expiration,
        currentPreviewTimeout: timeout,
      });
    },
    [dispatch],
  );

  const isDirty = useMemo(() => {
    return JSON.stringify(state) !== JSON.stringify(initialState);
  }, [state]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value) {
        if (isDirty) {
          showConfirmationDialog({
            title: "Are you sure you want to cancel?",
            description: "You will lose any unsaved changes.",
            intent: "primary",
            onConfirm: () => {
              dispatch({ type: "RESET" });
              navigate("/certifications");
            },
            onCancel: () => {},
          });
        } else {
          navigate("/certifications");
        }
      }
    },
    [isDirty, showConfirmationDialog, navigate],
  );

  const [createCertification] = useCreateCertificationMutation({
    onCompleted: ({ createCertification }) => {
      if (createCertification?.ok) {
        dispatch({ type: "CREATE_SUCCESS" });
        navigate("/certifications");
      } else {
        dispatch({
          type: "CREATE_COMPLETE",
          errorMessage: formatErrors(createCertification?.errors),
        });
      }
    },
    onError: (error) => {
      dispatch({
        type: "CREATE_COMPLETE",
        errorMessage:
          error.message || "An error occurred while creating the certification",
      });
    },
    refetchQueries,
  });

  const handleCreate = useCallback(async () => {
    if (!state.name.trim()) {
      dispatch({
        type: "SET_ERROR",
        errorMessage: "Certification name is required",
      });
      return;
    }

    dispatch({ type: "CREATE_START" });

    try {
      const validity = formatValidity(
        toExpiration(
          state.expiration.type,
          state.expiration.relativeExpirationInterval,
          state.expiration.relativeExpirationValue,
          state.expiration.absoluteExpiration,
        ),
      );

      const generate = await generateCertificateThumbnail();
      const thumbnail = await generate({
        name: state.name,
        description: state.description,
        logoUrl: workspace?.logo,
        companyName: workspace?.name,
        validity,
      });

      await createCertification({
        variables: {
          name: state.name,
          description: state.description,
          expirationType: state.expiration.type,
          expirationValue:
            state.expiration.type === ExpirationType.Relative
              ? state.expiration.relativeExpirationValue
              : undefined,
          expirationInterval:
            state.expiration.type === ExpirationType.Relative
              ? state.expiration.relativeExpirationInterval
              : undefined,
          expirationDate:
            state.expiration.type === ExpirationType.Absolute &&
            state.expiration.absoluteExpiration
              ? format(
                  new Date(state.expiration.absoluteExpiration),
                  "yyyy-MM-dd",
                )
              : undefined,
          thumbnail,
        },
      });
    } catch (error) {
      dispatch({
        type: "CREATE_COMPLETE",
        errorMessage:
          error instanceof Error
            ? error.message
            : "Failed to create certification",
      });
    }
  }, [state, createCertification, workspace]);

  return {
    name: state.name,
    description: state.description,
    expiration: state.expiration,
    preview: state.preview,
    onNameChange: handleNameChange,
    onDescriptionChange: handleDescriptionChange,
    onExpirationChange: handleExpirationChange,
    onOpenChange: handleOpenChange,
    onCreate: handleCreate,
    loading: state.loading,
    errorMessage: state.errorMessage,
    disabledText,
  };
}
